import { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Flex,
  Drawer,
  Portal,
  CloseButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useLocation } from 'wouter';
import { useImportSheet } from '@/contexts/ImportSheetContext';
import { ROUTES } from '@/types';
import ImportTabBar from './ImportTabBar';
import { TABS } from './importTabConfig';
import ImportUrlForm from './ImportUrlForm';
import ImportImagePanel from './ImportImagePanel';
import ImportTextPanel from './ImportTextPanel';
import ImportCollectionForm from './ImportCollectionForm';

export type ImportView = 'default' | 'url' | 'image' | 'text' | 'collection';

export default function ImportSheet() {
  const { isOpen, initialView, close } = useImportSheet();
  const [view, setView] = useState<ImportView>(initialView === 'default' ? 'url' : initialView);
  const [, navigate] = useLocation();

  // For rendering: no { ssr: false } (CLAUDE.md nav rule — returns correct value immediately)
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  // Sync view to initialView each time the sheet opens (ImportSheet never unmounts).
  useEffect(() => {
    if (isOpen) {
      setView(initialView === 'default' ? 'url' : (initialView as ImportView));
    }
  }, [isOpen, initialView]);

  // Close on Escape when the sheet is open.
  // Drawer handles this natively on mobile; explicit listener covers the desktop modal.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const handleTabChange = (tab: ImportView | 'manual') => {
    if (tab === 'manual') {
      close();
      navigate(ROUTES.RECIPES_NEW());
      return;
    }
    setView(tab as ImportView);
  };

  const isCollectionActive = view === 'collection';
  const panelTitle = isCollectionActive ? 'New collection' : 'Import a recipe';
  const hint = TABS.find((t) => t.key === view)?.hint;

  const tabBar = <ImportTabBar view={view} onTabChange={handleTabChange} />;

  const body = (
    <>
      {isCollectionActive && <ImportCollectionForm />}
      {!isCollectionActive && (
        <>
          {hint && (
            <Text fontFamily="body" fontWeight="400" fontSize="13px" color="text.primary" mb={4}>
              {hint}
            </Text>
          )}
          {view === 'url'   && <ImportUrlForm />}
          {view === 'image' && <ImportImagePanel />}
          {view === 'text'  && <ImportTextPanel />}
        </>
      )}
    </>
  );

  return (
    <>
      {/* ── Mobile: bottom drawer ── */}
      {!isDesktop && (
        <Drawer.Root
          open={isOpen}
          onOpenChange={(e) => { if (!e.open) close(); }}
          placement="bottom"
          lazyMount
          unmountOnExit
        >
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content
                bg="bg.canvas"
                borderTopRadius="20px"
                maxH="92dvh"
                overflow="hidden"
              >
                <Drawer.Header
                  bg="bg.canvas"
                  borderBottom="none"
                  px={5}
                  pt={3}
                  pb={0}
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={0}
                >
                  {/* Drag handle */}
                  <Box display="flex" justifyContent="center" mb={3} w="100%">
                    <Box w="36px" h="4px" borderRadius="2px" bg="border.muted" />
                  </Box>

                  {/* Title + close */}
                  <Flex justify="space-between" align="center" w="100%" mb={6}>
                    <Text fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
                      {panelTitle}
                    </Text>
                    <Drawer.CloseTrigger asChild>
                      <CloseButton size="sm" color="text.muted" />
                    </Drawer.CloseTrigger>
                  </Flex>

                  <Box w="100%" pb={4}>{tabBar}</Box>
                </Drawer.Header>

                <Drawer.Body px={5} pt={0} pb={4} overflowY="auto">
                  {body}
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      )}

      {/* ── Desktop: centred modal ── */}
      {isDesktop && isOpen && (
        <>
          {/* Backdrop — click outside to close, subtle blur on content behind */}
          <Box
            position="fixed"
            inset={0}
            zIndex={1000}
            onClick={close}
            backdropFilter="blur(3px)"
            bg="rgba(72, 60, 70, 0.12)"
          />

          {/* Modal */}
          <Box
            role="dialog"
            aria-modal="true"
            aria-label={panelTitle}
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="440px"
            bg="bg.canvas"
            borderRadius="16px"
            boxShadow="0 8px 40px rgba(72, 60, 70, 0.22)"
            border="1px solid"
            borderColor="border.muted"
            overflow="hidden"
            zIndex={1001}
          >
            <Box px={5} pt={5} pb={0}>
              <Flex justify="space-between" align="center" mb={5}>
                <Text fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
                  {panelTitle}
                </Text>
                <CloseButton size="sm" color="text.muted" onClick={close} />
              </Flex>
              {tabBar}
            </Box>

            <Box px={5} pt={4} pb={5} overflowY="auto" maxH="60vh">
              {body}
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
